"use client";

// Chapitre « Apprendre » : une session vidéo de la formation Claude Code,
// avec les vrais titres de sa partie 22 (« Créer un site web moderne avec
// Claude Code »). Le visiteur voit le lecteur tel qu'il est (vidéo, barre
// de visionnage, onglets, plan à droite).
//
// La vidéo doit se lire comme un cours filmé, pas comme un bloc de code :
// c'est un enregistrement d'écran, Claude Code à gauche, le navigateur à
// droite, les sous-titres du formateur en bas. Le portfolio de Léa apparaît
// d'abord en HTML brut, puis prend son allure quand la feuille de style
// arrive : la cause et l'effet se voient dans la même image.
//
// Le format 16/9 est écrit en valeur arbitraire : la configuration Tailwind
// du site ne fournit pas `aspect-video`.

import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useAt, useSpan, useStep, useTyped } from "../demoClock";
import { useTarget } from "../DemoCursor";
import Icon from "../icons";
import { BrandIcon, CourseIcon, StreakCapsule } from "../sceneParts";

const T = {
    play: 1000,
    typeStart: 1300,
    reply: 2850,
    html: 3250,
    css: 3950,
    done: 4550,
    videoEnd: 5300,
    tick: 6300,
    continue: 6950,
};

export const script = {
    duration: 7700,
    still: 5000,
    cursor: [
        { at: T.play, target: "course.play", click: true },
        // Une fois la vidéo lancée, la main se retire sous l'image pour
        // laisser voir le portfolio se construire.
        { at: 2100, target: "course.title", ax: 0.85, ay: 0.4, move: 900 },
        { at: T.continue, target: "course.continue", click: true, move: 900 },
    ],
};

const fade = {
    initial: { opacity: 0, y: 4 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.22, ease: "easeOut" },
};

const NAME = "Léa Morel";

function Terminal() {
    const t = useTranslations();
    const prompt = t("showcase_course_prompt");
    const typed = useTyped(prompt, T.typeStart, 28);
    const step = useStep([T.reply, T.html, T.css, T.done]);
    const typing = typed.length > 0 && typed.length < prompt.length;

    return (
        <div className="flex h-full flex-col overflow-hidden rounded-lg bg-[#0f1419] font-mono text-[11.5px] leading-[1.6] text-gray-300 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.5)] ring-1 ring-white/10">
            <div className="flex items-center gap-1.5 border-b border-white/5 px-3 py-2">
                <span className="h-2 w-2 rounded-full bg-white/15" />
                <span className="h-2 w-2 rounded-full bg-white/15" />
                <span className="h-2 w-2 rounded-full bg-white/15" />
                <span className="ml-2 text-[10px] text-gray-500">~/site</span>
            </div>
            <div className="space-y-1.5 px-3.5 py-3">
                <p className="mb-0 text-gray-500">
                    ~/site % <span className="text-gray-300">claude</span>
                </p>
                <p className="mb-0 text-gray-100">
                    <span className="mr-1.5 text-primary">&gt;</span>
                    {typed}
                    {typing && (
                        <span className="ml-px inline-block h-[1.05em] w-[0.55em] translate-y-[2px] bg-gray-300" />
                    )}
                </p>
                {step >= 1 && (
                    <motion.p {...fade} className="mb-0 pt-1">
                        <span className="mr-1.5 text-[#D97757]">●</span>
                        {t("showcase_course_reply")}
                    </motion.p>
                )}
                {step >= 2 && (
                    <motion.p {...fade} className="mb-0 pl-4 text-gray-400">
                        <span className="mr-1.5 text-emerald-400">✓</span>index.html
                    </motion.p>
                )}
                {step >= 3 && (
                    <motion.p {...fade} className="mb-0 pl-4 text-gray-400">
                        <span className="mr-1.5 text-emerald-400">✓</span>styles.css
                    </motion.p>
                )}
                {step >= 4 && (
                    <motion.p {...fade} className="mb-0 pt-1">
                        <span className="mr-1.5 text-[#D97757]">●</span>
                        {t("showcase_course_done")}
                    </motion.p>
                )}
            </div>
        </div>
    );
}

// Le portfolio tel que le navigateur l'affiche : vide, puis en HTML brut
// (police par défaut, lien bleu souligné), puis mis en forme.
function Portfolio({ compact }) {
    const t = useTranslations();
    const stage = useStep([T.html, T.css]);
    const projects = ["showcase_course_project_1", "showcase_course_project_2", "showcase_course_project_3"];

    return (
        <div className="flex h-full flex-col overflow-hidden rounded-lg bg-white shadow-[0_12px_32px_-8px_rgba(0,0,0,0.5)] ring-1 ring-black/10">
            <div className={`flex items-center gap-1.5 border-b border-gray-200 bg-gray-100 ${compact ? "px-2 py-1" : "px-3 py-1.5"}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                <span
                    className={`mx-auto rounded bg-white px-2 text-gray-400 ${compact ? "text-[7.5px]" : "text-[9.5px]"}`}
                >
                    index.html
                </span>
            </div>
            <div className="relative min-h-0 flex-1">
                <AnimatePresence initial={false}>
                    {stage === 1 && (
                        <motion.div
                            key="raw"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className={`absolute inset-0 bg-white text-black ${compact ? "p-2.5 text-[8px]" : "p-3.5 text-[10.5px]"}`}
                            style={{ fontFamily: "'Times New Roman', Times, serif" }}
                        >
                            <p className={`mb-0 font-bold ${compact ? "text-[15px]" : "text-[21px]"}`}>{NAME}</p>
                            <p className="mb-0 mt-1">{t("showcase_challenge_site_job")}</p>
                            <ul className={`mb-0 list-disc ${compact ? "mt-1.5 pl-3.5" : "mt-2 pl-5"}`}>
                                {projects.map((key) => (
                                    <li key={key} className="mb-0">
                                        {t(key)}
                                    </li>
                                ))}
                            </ul>
                            <p className="mb-0 mt-1.5 text-[#0000EE] underline">{t("showcase_challenge_site_cta")}</p>
                        </motion.div>
                    )}
                    {stage === 2 && (
                        <motion.div
                            key="styled"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.45, ease: "easeOut" }}
                            className={`absolute inset-0 bg-[#FBF8F3] ${compact ? "p-3" : "p-4"}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className={`font-sourceSerif font-semibold text-[#1c1917] ${compact ? "text-[8px]" : "text-[10px]"}`}>
                                    {NAME}
                                </span>
                                <span className={`rounded-full bg-[#1f4e4a] font-semibold text-white ${compact ? "px-1.5 py-0.5 text-[6.5px]" : "px-2 py-0.5 text-[8.5px]"}`}>
                                    {t("showcase_challenge_site_cta")}
                                </span>
                            </div>
                            <p className={`mb-0 font-sourceSerif leading-[1.05] text-[#1c1917] ${compact ? "mt-2 text-[16px]" : "mt-4 text-[24px]"}`}>
                                {t("showcase_course_site_headline")}
                            </p>
                            <p className={`mb-0 text-[#57534e] ${compact ? "mt-1 text-[7.5px]" : "mt-1.5 text-[10px]"}`}>
                                {t("showcase_challenge_site_job")}
                            </p>
                            <div className={`grid grid-cols-3 ${compact ? "mt-2.5 gap-1.5" : "mt-4 gap-2"}`}>
                                <span className="aspect-[4/5] rounded bg-[#E8D5C4]" />
                                <span className="aspect-[4/5] rounded bg-[#C9D8D2]" />
                                <span className="aspect-[4/5] rounded bg-[#D9D4E7]" />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

// Les sous-titres du formateur, une phrase par étape de la démonstration.
function Captions({ compact }) {
    const t = useTranslations();
    const step = useStep([T.play, T.reply, T.done]);
    const key = ["", "showcase_course_caption_1", "showcase_course_caption_2", "showcase_course_caption_3"][step];

    return (
        <div className={`pointer-events-none absolute inset-x-0 flex justify-center ${compact ? "bottom-[22px]" : "bottom-[42px]"}`}>
            <AnimatePresence mode="wait">
                {key && (
                    <motion.span
                        key={key}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className={`rounded bg-black/75 text-center text-white ${
                            compact ? "max-w-[92%] px-1.5 py-0.5 text-[8.5px]" : "px-2.5 py-1 text-[13px]"
                        }`}
                    >
                        {t(key)}
                    </motion.span>
                )}
            </AnimatePresence>
        </div>
    );
}

function Video({ compact }) {
    const t = useTranslations();
    const playRef = useTarget("course.play");
    const continueRef = useTarget("course.continue");
    const playing = useAt(T.play);
    const ended = useAt(T.videoEnd);
    const seconds = useAt(T.tick) ? 2 : 3;
    const continued = useAt(T.continue);
    const watched = useSpan(T.play, T.videoEnd);

    return (
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-[#1d2129] ring-1 ring-black/5 dark:ring-white/5">
            {/* L'écran filmé : un fond de bureau, deux fenêtres. */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#343a48] via-[#262b35] to-[#1b1f27]" />
            {compact ? (
                <div className="absolute inset-x-[10%] bottom-[34px] top-[10px]">
                    <Portfolio compact />
                </div>
            ) : (
                <div className="absolute inset-x-5 bottom-[76px] top-5 flex gap-3">
                    <div className="w-[46%]">
                        <Terminal />
                    </div>
                    <div className="min-w-0 flex-1">
                        <Portfolio />
                    </div>
                </div>
            )}

            <Captions compact={compact} />

            {/* Commandes du lecteur */}
            <div
                className={`absolute inset-x-0 bottom-0 flex items-center bg-gradient-to-t from-black/60 to-transparent text-white/90 ${
                    compact ? "gap-2 px-2.5 pb-1.5 pt-3" : "gap-3 px-4 pb-3 pt-6"
                }`}
            >
                <Icon name={playing ? "pause" : "play"} className={compact ? "h-2.5 w-2.5" : "h-4 w-4"} />
                <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-white/25">
                    <motion.div
                        className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-white"
                        style={{ scaleX: watched }}
                    />
                </div>
                <span className={`tabular-nums text-white/80 ${compact ? "text-[8px]" : "text-[11px]"}`}>12:00</span>
                {!compact && (
                    <>
                        <Icon name="subtitles" className="h-4 w-4 text-white" />
                        <Icon name="volume" className="h-4 w-4 text-white/80" />
                        <Icon name="settings" className="h-4 w-4 text-white/80" />
                        <Icon name="fullscreen" className="h-4 w-4 text-white/80" />
                    </>
                )}
            </div>

            {/* Avant la lecture : l'image figée et le bouton du lecteur */}
            <AnimatePresence>
                {!playing && (
                    <motion.div
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 flex items-center justify-center bg-black/35"
                    >
                        <span
                            ref={playRef}
                            className={`flex items-center justify-center rounded-xl bg-black/70 text-white shadow-lg ${
                                compact ? "h-9 w-14" : "h-12 w-[72px]"
                            }`}
                        >
                            <Icon name="play" className={compact ? "ml-0.5 h-4 w-4" : "ml-0.5 h-5 w-5"} />
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Fin de la vidéo : l'écran « Session suivante » */}
            <AnimatePresence>
                {ended && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="absolute inset-0 flex flex-col items-center justify-center bg-[#0f1419]/85 text-center backdrop-blur-[2px]"
                    >
                        <p className={`mb-0 tabular-nums text-white/60 ${compact ? "text-[10px]" : "text-[13px]"}`}>
                            {t("showcase_course_next_in", { seconds })}
                        </p>
                        <p
                            className={`mb-0 mt-1.5 max-w-[80%] font-semibold leading-snug text-white ${
                                compact ? "text-[12px]" : "text-lg"
                            }`}
                        >
                            {t("showcase_course_s3")}
                        </p>
                        <div className={`flex items-center ${compact ? "mt-3 gap-2" : "mt-5 gap-3"}`}>
                            <span
                                className={`rounded-lg bg-white/10 font-semibold text-white/80 ${
                                    compact ? "px-3 py-1.5 text-[10px]" : "px-4 py-2 text-[13px]"
                                }`}
                            >
                                {t("showcase_course_cancel")}
                            </span>
                            <span
                                ref={continueRef}
                                className={`flex items-center gap-1 rounded-lg font-semibold text-white transition-colors duration-150 ${
                                    continued ? "bg-[#c9441a]" : "bg-primary"
                                } ${compact ? "px-3 py-1.5 text-[10px]" : "px-4 py-2 text-[13px]"}`}
                            >
                                {t("showcase_course_continue")}
                                <Icon name="arrow-right" className={compact ? "h-3 w-3" : "h-4 w-4"} />
                            </span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// Les trois sessions de la partie 22, avec leur durée réelle arrondie.
const SESSIONS = [
    { key: "s1", minutes: 4 },
    { key: "s2", minutes: 12 },
    { key: "s3", minutes: 3 },
];

function SessionRow({ session, state, compact }) {
    const t = useTranslations();
    const done = state === "done";
    const active = state === "active";

    return (
        <li
            className={`mb-0 flex items-start gap-2.5 rounded-lg transition-colors duration-300 ${
                compact ? "px-2 py-1.5" : "px-2.5 py-2"
            } ${active ? "bg-primary/[0.08]" : ""}`}
        >
            <span
                className={`relative mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-300 ${
                    done
                        ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-400/10"
                        : active
                          ? "border-primary bg-primary/10"
                          : "border-gray-200 dark:border-grayDark-700"
                }`}
            >
                {done ? (
                    <motion.svg
                        key="check"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        viewBox="0 0 24 24"
                        className="h-3 w-3 text-emerald-500"
                    >
                        <path
                            d="M5 12.5l4.5 4.5L19 7.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </motion.svg>
                ) : active ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                ) : null}
            </span>
            <span
                className={`line-clamp-2 min-w-0 flex-1 leading-snug ${compact ? "text-[11.5px]" : "text-[12.5px]"} ${
                    done
                        ? "text-gray-400 dark:text-grayDark-500"
                        : active
                          ? "font-medium text-gray-900 dark:text-white"
                          : "text-gray-600 dark:text-grayDark-300"
                }`}
            >
                {t(`showcase_course_${session.key}`)}
            </span>
            <span className="mt-px shrink-0 text-[11px] tabular-nums text-gray-300 dark:text-grayDark-600">
                {session.minutes} min
            </span>
        </li>
    );
}

function PartCard({ number, titleKey, done, total, status, children, compact }) {
    const t = useTranslations();
    const bar = status === "done" ? "bg-emerald-400" : status === "current" ? "bg-primary" : "bg-transparent";

    return (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white dark:border-grayDark-800 dark:bg-bgDark-700">
            <div className={`h-[3px] ${bar}`} />
            <div className={compact ? "px-3 py-2" : "px-3.5 py-2.5"}>
                <p className="mb-0 text-[10px] uppercase tracking-widest text-gray-400 dark:text-grayDark-500">
                    {t("showcase_course_part", { number })}
                </p>
                <p className="mb-0 mt-0.5 text-[14px] font-semibold leading-snug text-gray-900 dark:text-white">{t(titleKey)}</p>
                <p className="mb-0 mt-0.5 text-[11px] text-gray-400 dark:text-grayDark-500">
                    {t("showcase_course_sessions_count", { done, total })}
                </p>
            </div>
            {children}
        </div>
    );
}

function Plan({ compact }) {
    const ended = useAt(T.videoEnd);
    const states = ended ? ["done", "done", "active"] : ["done", "active", "todo"];

    const list = (
        <ul className={compact ? "space-y-0.5 px-1.5 pb-1.5" : "space-y-0.5 px-2 pb-2"}>
            {SESSIONS.map((session, index) => (
                <SessionRow key={session.key} session={session} state={states[index]} compact={compact} />
            ))}
        </ul>
    );

    if (compact) {
        return (
            <PartCard number={22} titleKey="showcase_course_part22" done={ended ? 2 : 1} total={3} status="current" compact>
                {list}
            </PartCard>
        );
    }

    return (
        <div className="space-y-3">
            <PartCard number={21} titleKey="showcase_course_quiz" done={4} total={4} status="done" />
            <PartCard number={22} titleKey="showcase_course_part22" done={ended ? 2 : 1} total={3} status="current">
                {list}
            </PartCard>
            <PartCard number={23} titleKey="showcase_course_quiz" done={0} total={4} status="todo" />
        </div>
    );
}

function PlanHeader() {
    const t = useTranslations();
    const validated = useAt(T.videoEnd);

    return (
        <div className="mb-4 flex items-center gap-1.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-400 dark:border-grayDark-700 dark:bg-bgDark-700 dark:text-grayDark-400">
                <Icon name="arrow-left" className="h-4 w-4" />
            </span>
            <span
                className={`flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl text-[12.5px] font-semibold transition-colors duration-300 ${
                    validated
                        ? "border border-gray-200 bg-white text-gray-600 dark:border-grayDark-700 dark:bg-bgDark-700 dark:text-grayDark-300"
                        : "bg-primary text-white shadow-md shadow-primary/20"
                }`}
            >
                <Icon name={validated ? "reset" : "check"} className="h-4 w-4 shrink-0" />
                <span className="truncate">
                    {validated ? t("showcase_course_reset") : t("showcase_course_validate")}
                </span>
            </span>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-400 dark:border-grayDark-700 dark:bg-bgDark-700 dark:text-grayDark-400">
                <Icon name="arrow-right" className="h-4 w-4" />
            </span>
        </div>
    );
}

function WatchedBar() {
    const watched = useSpan(T.play, T.videoEnd);
    return (
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-200 dark:bg-grayDark-800">
            <motion.div className="h-full w-full origin-left bg-primary" style={{ scaleX: watched }} />
        </div>
    );
}

const TABS = ["progress", "content", "sources", "discussions", "tasks"];

export default function CourseScene({ compact }) {
    const t = useTranslations();
    const titleRef = useTarget("course.title");

    const title = (
        <div ref={titleRef} className={compact ? "mt-3" : "mt-4"}>
            <p className="mb-0 text-[10px] uppercase tracking-widest text-gray-400 dark:text-grayDark-500">
                {t("showcase_course_session_label", { part: 22, session: 2 })}
            </p>
            <p
                className={`mb-0 mt-1 font-bold text-gray-900 dark:text-white ${
                    compact ? "text-[15px] leading-snug" : "text-lg"
                }`}
            >
                {t("showcase_course_s2")}
            </p>
        </div>
    );

    if (compact) {
        return (
            <div className="flex h-full flex-col bg-white dark:bg-bgDark-800">
                <header className="flex h-12 shrink-0 items-center gap-2.5 border-b border-gray-100 px-4 dark:border-grayDark-800">
                    <Icon name="arrow-left" className="h-4 w-4 text-gray-400" />
                    <CourseIcon className="h-6 w-6" />
                    <span className="text-[14px] font-semibold text-gray-900 dark:text-white">Claude Code</span>
                    <StreakCapsule days={12} small className="ml-auto" />
                </header>
                <div className="px-4 pt-4">
                    <Video compact />
                    <WatchedBar />
                    {title}
                </div>
                <div className="mt-4 px-4">
                    <Plan compact />
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col bg-white dark:bg-bgDark-800">
            <header className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-100 px-5 dark:border-grayDark-800">
                <BrandIcon className="h-6 w-6" />
                <span className="h-5 w-px bg-gray-200 dark:bg-grayDark-700" />
                <CourseIcon className="h-6 w-6" />
                <span className="text-[15px] font-semibold text-gray-900 dark:text-white">Claude Code</span>
                <StreakCapsule days={12} className="ml-auto" />
            </header>

            <div className="flex min-h-0 flex-1">
                <main className="min-w-0 flex-1 px-5 pt-5">
                    <Video />
                    <WatchedBar />
                    {title}
                    <nav className="mt-3 flex gap-6 border-b border-gray-100 text-[13px] dark:border-grayDark-800">
                        {TABS.map((tab) => (
                            <span
                                key={tab}
                                className={`relative pb-2.5 ${
                                    tab === "content"
                                        ? "font-medium text-primary"
                                        : "text-gray-500 dark:text-grayDark-400"
                                }`}
                            >
                                {t(`showcase_course_tab_${tab}`)}
                                {tab === "content" && (
                                    <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary" />
                                )}
                            </span>
                        ))}
                    </nav>
                </main>

                <aside className="w-[288px] shrink-0 border-l border-gray-200 bg-gray-50/60 p-4 dark:border-grayDark-800 dark:bg-bgDark-900/40">
                    <PlanHeader />
                    <Plan />
                </aside>
            </div>
        </div>
    );
}
